import { usePage } from '@inertiajs/react'

import Layout from '@/components/Layout'
import { PaymentMarks } from '@/components/PaymentMarks'
import { interpolate, renderRich, useTranslation } from '@/lib/i18n'
import type { SharedProps } from '@/types'

interface Block {
  type: 'p' | 'ul'
  text?: string
  items?: string[]
}

interface Panel {
  heading: string
  blocks: Block[]
}

/**
 * Payment conditions for the same sole trader in both storefront languages.
 *
 * The acquirer dictates a long block of wording and a sample of the document
 * that confirms payment. Printed flat it is a wall of text, so the page
 * leads with a short summary and the card marks, then folds the mandatory
 * paragraphs into disclosures; the sample receipt is drawn as a receipt
 * instead of described in a sentence.
 */
export default function PaymentPage() {
  const { seller } = usePage<SharedProps>().props
  const { t, tx } = useTranslation()

  // The component can also be mounted in isolated previews without shared props.
  if (!seller) return null

  const vars = {
    phone: seller.phone,
    phoneHref: seller.phoneHref,
    email: seller.email,
    acquirer: seller.acquirer,
    name: seller.name,
    unp: seller.unp,
  }

  const rich = (text: string) => renderRich(interpolate(text, vars))
  const panels = tx<Panel[]>('payment.panels') ?? []
  const receipt = tx<Array<{ label: string; value: string }>>('payment.receipt.rows') ?? []

  return (
    <Layout showBack>
      <article className="mx-auto w-full max-w-[720px] px-8 pt-16 max-[559px]:px-4 max-[559px]:pt-10">
        <header className="border-b border-hairline pb-5">
          <div className="mb-2.5 text-[13px] text-ink-faint">{t('payment.eyebrow')}</div>
          <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-0.02em] max-[559px]:text-[24px]">
            {t('payment.title')}
          </h1>
          <p className="mt-3 max-w-[64ch] text-[15px] leading-[1.6] text-ink-muted">
            {rich(t('payment.intro'))}
          </p>
          {!seller.cardPaymentsAvailable && (
            <p className="mt-3 rounded-xl border border-hairline bg-surface px-4 py-3 text-sm leading-relaxed text-ink-muted">
              {t('payment.pending')}
            </p>
          )}
          <PaymentMarks className="mt-5" />
        </header>

        {/* The wording the acquirer dictates, split into short titled
            sections inside one card. Deliberately NOT an accordion: a
            collapsed Radix panel keeps its text out of the DOM, and this
            text has to be readable — and findable — on the page itself. */}
        <section className="mt-10">
          <h2 className="mb-3 text-[16px] font-bold">{t('payment.details_title')}</h2>
          <div className="divide-y divide-hairline rounded-[12px] border border-hairline">
            {panels.map((panel) => (
              <section key={panel.heading} className="px-5 py-4 max-[559px]:px-4">
                <h3 className="mb-2 text-[15px] font-semibold">{panel.heading}</h3>
                {panel.blocks.map((block, blockIndex) =>
                  block.type === 'ul' ? (
                    <ul
                      key={blockIndex}
                      className="mb-2.5 list-disc pl-5 text-[14.5px] leading-[1.6] text-ink-muted marker:text-ink-faint last:mb-0"
                    >
                      {(block.items ?? []).map((item, itemIndex) => (
                        <li key={itemIndex} className="mb-1.5">
                          {rich(item)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p
                      key={blockIndex}
                      className="mb-2.5 text-[14.5px] leading-[1.6] text-ink-muted last:mb-0"
                    >
                      {rich(block.text ?? '')}
                    </p>
                  )
                )}
              </section>
            ))}
          </div>
        </section>

        {/* Образец документа, подтверждающего оплату. */}
        <section className="mt-12">
          <h2 className="mb-1 text-[16px] font-bold">{t('payment.receipt.title')}</h2>
          <p className="mb-4 max-w-[64ch] text-[15px] leading-[1.6] text-ink-muted">
            {rich(t('payment.receipt.intro'))}
          </p>

          <div className="mx-auto max-w-[420px] rounded-[12px] border border-hairline bg-surface p-5">
            <div className="border-b border-dashed border-hairline pb-3 text-center">
              <div className="text-[14px] font-bold text-ink">{t('payment.receipt.header')}</div>
              <div className="mt-1 text-[12px] text-ink-faint">{seller.name}</div>
              <div className="text-[12px] text-ink-faint">
                {t('requisites.unp')}: {seller.unp}
              </div>
            </div>

            <dl className="py-3 text-[12.5px] leading-[1.5]">
              {receipt.map((row) => (
                <div key={row.label} className="flex justify-between gap-4 py-[3px]">
                  <dt className="text-ink-faint">{row.label}</dt>
                  <dd className="text-right font-medium text-ink-muted">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="border-t border-dashed border-hairline pt-3 text-center text-[12px] text-ink-faint">
              {t('payment.receipt.footer')}
            </div>
          </div>

          <p className="mx-auto mt-3 max-w-[420px] text-[12.5px] leading-[1.5] text-ink-faint">
            {t('payment.receipt.note')}
          </p>
        </section>

        <footer className="mt-12 border-t border-hairline pt-6">
          <div className="mb-2 text-[13px] text-ink-faint">{t('payment.support_title')}</div>
          <p className="max-w-[64ch] text-[15px] leading-[1.6] text-ink-muted">
            {seller.name}
            <br />
            {seller.address}
            <br />
            <a href={`tel:${seller.phoneHref}`} className="hover:text-ink">
              {seller.phone}
            </a>
            {' · '}
            <a href={`mailto:${seller.email}`} className="hover:text-ink">
              {seller.email}
            </a>
            <br />
            {seller.hours}
          </p>
        </footer>
      </article>
    </Layout>
  )
}
